import { EnergyIcon } from '../types/energy.types';

interface EnergyWheelProps {
  icons: EnergyIcon[];
  selectedIcons?: Set<string>;
}

export const EnergyWheel = ({ icons, selectedIcons }: EnergyWheelProps) => {
  const radius = 145; // Distance from center to icons
  const centerX = 180;
  const centerY = 180;
  const viewBoxSize = 360;

  return (
    <div className="flex flex-col items-center gap-4">
      <div
        className="relative mx-auto"
        style={{ width: '360px', height: '380px' }}
      >
        {/* SVG for connection lines */}
        <svg
          className="absolute left-0 top-0"
          width={viewBoxSize}
          height={viewBoxSize}
          viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        >
          {icons.map((icon, index) => {
            const angle = (index * 360) / icons.length - 90; // Start from top
            const radian = (angle * Math.PI) / 180;
            const x = centerX + radius * Math.cos(radian);
            const y = centerY + radius * Math.sin(radian);
            const isSelected = selectedIcons?.has(icon.id);

            return (
              <line
                key={`line-${icon.id}`}
                x1={centerX}
                y1={centerY}
                x2={x}
                y2={y}
                stroke={isSelected ? '#3b82f6' : '#d1d5db'}
                strokeWidth={isSelected ? '2' : '1'}
                strokeDasharray={isSelected ? '0' : '4 2'}
                opacity={isSelected ? '0.6' : '0.3'}
              />
            );
          })}
        </svg>

        {/* Center circle with label */}
        <div
          className="bg-primary-2 text-mono-0 absolute flex h-24 w-24 flex-col items-center justify-center rounded-full shadow-lg"
          style={{
            left: `${centerX - 48}px`,
            top: `${centerY - 48}px`,
          }}
        >
          <div className="text-xs font-semibold">ENERGY</div>
          <div className="text-xs font-semibold">WHEEL</div>
          <div className="text-[10px] opacity-80">{icons.length} Types</div>
        </div>

        {/* Energy icons positioned in circle */}
        {icons.map((icon, index) => {
          const angle = (index * 360) / icons.length - 90; // Start from top
          const radian = (angle * Math.PI) / 180;
          const x = centerX + radius * Math.cos(radian);
          const y = centerY + radius * Math.sin(radian);

          const isSelected = selectedIcons?.has(icon.id);

          return (
            <div
              key={icon.id}
              className="absolute flex -translate-x-1/2 -translate-y-1/2 transform flex-col items-center gap-1"
              style={{
                left: `${x}px`,
                top: `${y}px`,
                width: '80px',
              }}
            >
              {/* Icon bubble */}
              <div
                className={`bg-mono-0 flex h-12 w-12 items-center justify-center rounded-full border-2 shadow-md transition-all ${
                  isSelected
                    ? 'border-primary-3 bg-primary-5 scale-110'
                    : 'border-gray-3'
                }`}
                title={icon.title}
              >
                <span className="text-2xl">{icon.emoji}</span>
              </div>

              {/* Label */}
              <div
                className={`text-center text-[11px] font-medium leading-tight ${
                  isSelected ? 'text-primary-2 font-semibold' : 'text-gray-1'
                }`}
              >
                {icon.title}
              </div>
            </div>
          );
        })}
      </div>

      <div className="text-gray-2 max-w-lg text-center text-xs">
        <p className="mb-2">
          <strong>The Energy Wheel</strong> helps identify all hazard types
          systematically. Research shows this framework improves hazard
          recognition by 30% compared to traditional checklists.
        </p>
        <p className="text-[10px] italic">
          Based on Hallowell Energy-Based Safety research
        </p>
      </div>
    </div>
  );
};
